<template>
  <v-container fluid>
    <v-row class="common-card-row">
      <v-col cols="12" md="3" class="pa-0 pb-3 pb-md-0">
        <v-col class="pa-0 filters-con">
          <v-card
            flat
            class="filter-con d-flex justify-space-between pa-4 mb-4 justify-"
            v-for="(item, index) in page_content.rollout_overview_stats"
            :key="`${item.value}_${index}`"
            @click="updateView(item.value)"
            :elevation="`${item.value === current_view ? 3 : 0}`"
          >
            <div class="ma-1">
              <div class="text-caption">
                {{ item.label }}
              </div>
              <div class="primary--text text-h6">
                {{
                  item.type == "number"
                    ? $numberWithCommas(current_stat[item.value].count)
                    : current_stat[item.value].count
                }}
              </div>
              <div v-if="item.subtext">
                <v-chip :color="`${item.color} display-4 `" :text-color="item.color">
                  <span class="text-caption text--secondary"
                    >{{ current_stat[item.value].installed }}{{ item.subtext }}</span
                  >
                </v-chip>
              </div>
            </div>
            <div class="ma-1 mr-0">
              <v-chip class="opaque-img px-4 py-7" :color="`${item.color} lighten-4`" label :text-color="item.color">
                <v-icon>{{ item.icon }}</v-icon>
              </v-chip>
            </div>
          </v-card>
        </v-col>
      </v-col>
      <v-col cols="12" md="3" class="pa-0 pb-3 pb-md-0 pl-md-3">
        <v-col
          v-if="current_view == 'devices' && devices_data_available && status_data && chartColors"
          class="common-card-col"
          cols="12"
          key="devices_container"
        >
          <RadialChartVT
            :chartData="status_data"
            :title="$t('views.RolloutDashboard.charts.statusUpdate.title')"
            :subtitle="devices.length"
            @on-click="apply_donut_filter($event)"
            :customColors="chartColors"
          />
        </v-col>
        <v-col
          v-else-if="current_view == 'planner_groups' && planner_group_donut"
          class="common-card-col"
          cols="12"
          key="planner_groups_container"
        >
          <RadialChartVT
            :chartData="planner_group_donut"
            :title="$t('views.RolloutDashboard.charts.plannerGroup.title')"
            @on-click="apply_group_donut_filter"
            :pieHole="pieHole"
          />
        </v-col>
        <v-col
          v-else-if="current_view == 'sites' && formatted_sites && formatted_sites.length > 0 && site_status_data"
          class="common-card-col"
          cols="12"
          key="sites_container"
        >
          <RadialChartVT
            :chartData="site_status_data"
            :subtitle="formatted_sites.length"
            :title="$t('views.RolloutDashboard.charts.siteStatusUpdate.title')"
            @on-click="apply_site_donut_filter"
          />
        </v-col>
      </v-col>
      <v-col cols="12" md="6" class="pa-0 pl-md-3">
        <v-col class="common-card-col" cols="12">
          <div class="d-flex justify-space-between">
            <div class="title titleLight--text pt-3">
              {{
                current_view == "devices"
                  ? $t("views.RolloutDashboard.charts.deviceStatus.title")
                  : $t("views.RolloutDashboard.charts.overview.title")
              }}
            </div>
            <div class="pa-1" v-if="devices_data_available && current_view == 'devices'">
              <v-chip class="ma-2" color="primary">
                <v-progress-circular
                  :value="(installed_devices / devices.length) * 100"
                  :size="20"
                  color="secondary"
                ></v-progress-circular>
                <span
                  ><span class="pl-2 text-h6">{{ installed_devices }}</span>
                  <span class="pr-2 primary--text text--lighten-5 text-caption">/{{ devices.length }}</span></span
                >
                <span class="text-subtitle-1"><b>Installed</b></span>
              </v-chip>
            </div>
          </div>
          <ColumnChartVT
            v-if="current_view != 'devices'"
            :chartData="planner_group_status"
            :chart_title="$t('views.RolloutDashboard.charts.plannerGroup.title')"
            @on-click="apply_group_donut_filter"
            lineWidth="1"
            pointSize="1"
            dataOpacity="1"
          >
          </ColumnChartVT>
          <ComboChartVT
            v-else
            :chartData="overview_data"
            ylabel="Number of Devices"
            xlabel="Month-Year"
            lineWidth="2"
            pointSize="4"
            dataOpacity="1"
            :colors="page_content.rollout_chart.colors"
          ></ComboChartVT>
        </v-col>
      </v-col>
    </v-row>
    <v-row class="common-card-row" :align="`stretch`">
      <v-col cols="12" md="12" class="pa-0 pb-3">
        <v-col class="common-card-col" cols="12">
          <v-card flat>
            <v-card-title class="titleLight--text title">
              {{
                current_view == "devices"
                  ? $t("views.RolloutDashboard.tables.devices.title")
                  : current_view == "planner_groups"
                    ? $t("views.RolloutDashboard.tables.plannerGroups.title")
                    : $t("views.RolloutDashboard.tables.sites.title")
              }}<span v-if="filtered_data && planner_groups[selected_group]" class="text-caption ml-1"
                >(<span class="text-h6 primaryOutline--text">{{ filtered_data && filtered_data.length }}</span
                >/ {{ planner_groups[selected_group] }})</span
              >
            </v-card-title>
            <v-card-subtitle class="mt-2 text-caption">
              <v-row no-gutters>
                <v-col cols="6" v-if="current_view == 'devices'">
                  <v-chip
                    v-for="fil in status_keys"
                    :key="fil"
                    class="ma-2"
                    :close="fil == filter"
                    :color="$config.rollout_status[fil]"
                    label
                    @click="apply_donut_filter(fil)"
                    @click:close="filter = null"
                    small
                    text-color="white"
                  >
                    {{ fil }}
                  </v-chip>
                  <v-chip
                    v-if="selected_group != 'All'"
                    class="ma-2"
                    close
                    color="lightYelow"
                    label
                    @click:close="selected_group = 'All'"
                    small
                    text-color="white"
                  >
                    {{ selected_group }}
                  </v-chip>
                </v-col>
                <v-spacer></v-spacer>
                <v-col cols="auto" v-if="current_view != 'planner_groups'">
                  <v-select
                    v-model="selected_group"
                    :items="all_planner_groups"
                    label="Planner Group"
                    dense
                    outlined
                  ></v-select>
                </v-col>
              </v-row>
            </v-card-subtitle>
            <v-card-text class="pa-0">
              <v-row no-gutters>
                <v-col :cols="group_overview_data && group_overview_data.length > 1 ? 7 : 12" class="right-border">
                  <DataTableVT
                    v-if="current_view == 'devices' && filtered_data"
                    :headers="page_content.rollout_table"
                    :items_list="filtered_data"
                    :sort_asc="sortDesc"
                    sort_by="DeviceStatus"
                    item_key="DeviceID"
                    :loading="is_loading"
                    @action="redirect_user($event)"
                  />
                  <DataTableVT
                    v-if="current_view == 'planner_groups' && planner_groups_list"
                    :headers="page_content.rollout_planner_groups_table"
                    :items_list="planner_groups_list"
                    :sort_asc="sortDesc"
                    sort_by="DeviceStatus"
                    item_key="DeviceID"
                    :loading="is_loading"
                    @action="redirect_user($event)"
                  />
                  <DataTableVT
                    v-if="current_view == 'sites' && filtered_sites"
                    :headers="page_content.rollout_sites_table"
                    :items_list="filtered_sites"
                    :sort_asc="sortDesc"
                    sort_by="DeviceStatus"
                    item_key="DeviceID"
                    :loading="is_loading"
                    @action="redirect_user($event)"
                  />
                </v-col>
                <v-col cols="4" md="5" class="pl-0 pl-md-3 pr-0 pt-0">
                  <v-col class="common-card-col" cols="12" v-if="map_data">
                    <div v-if="selected_group == 'All'">
                      <div v-if="current_view == 'devices' && (!filter || filter == 'Installed')">
                        <MapsVT
                          :heat_map_on="add_heat_map"
                          :options="page_content.maps_rollout_config"
                          :items="map_data"
                          :height="selected_group != 'All' ? '35vh' : '70vh'"
                        />
                      </div>
                      <div v-else-if="current_view != 'devices'">
                        <MapsVT
                          :heat_map_on="add_heat_map"
                          :options="page_content.maps_sites_rollout_config"
                          :items="map_formatted_sites"
                          :height="selected_group != 'All' ? '35vh' : '70vh'"
                        />
                      </div>
                    </div>
                    <div
                      class="pt-4"
                      v-if="
                        selected_group != 'All' &&
                        devices_data_available &&
                        group_overview_data &&
                        group_overview_data.length > 1
                      "
                    >
                      <div class="d-flex justify-space-between">
                        <div class="text-subtitle-1 titleLight--text pt-3">
                          <span class="primary--text">{{ selected_group }}:</span>
                          {{ $t("views.RolloutDashboard.charts.deviceStatus.title") }}
                        </div>
                      </div>
                      <ComboChartVT
                        :chartData="group_overview_data"
                        ylabel="Number of Devices"
                        xlabel="Month-Year"
                        lineWidth="2"
                        pointSize="4"
                        dataOpacity="1"
                        :colors="page_content.rollout_chart.colors"
                      ></ComboChartVT>
                    </div>
                  </v-col>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-col>
    </v-row>
    <v-snackbar color="borderDark" v-model="snackbar" timeout="2000">{{ message }}</v-snackbar>
  </v-container>
</template>

<script>
import MapsVT from "../components/MapsVT";
import DataTableVT from "../components/DataTableVT";
import RadialChartVT from "../components/RadialChartVT";
import ColumnChartVT from "../components/ColumnChartVT";
import ComboChartVT from "../components/ComboChartVT";
import { axios_get } from "../services/vt_service";
import { scv } from "../services/powerdata";
import { content } from "../../config/scavenger.js";
import { mobile_grpc_services } from "../services/MobileServices";
import { scavenger_grpc_services } from "../services/ScavengerServices";

export default {
  name: "RolloutDashboard",
  components: {
    MapsVT,
    DataTableVT,
    RadialChartVT,
    ColumnChartVT,
    ComboChartVT,
  },
  data: function () {
    return {
      devices: null,
      message: "",
      is_loading: false,
      snackbar: false,
      add_heat_map: true,
      filter: null,
      sortDesc: true,
      planner_groups: null,
      asset_id_lookup: null,
      total_planner_groups: 0,
      selected_group: "All",
      sites: null,
      current_view: "devices",
      selected_sites: null,
      pieHole: 0.01,
    };
  },
  created() {
    this.page_content = content;
  },
  mounted() {
    this.get_device_list();
    this.get_sites();
  },
  methods: {
    async get_device_list() {
      this.network_error = false;
      // let response = await axios_get(scv.get_scavenger_list);
      let response = await scavenger_grpc_services.GetScavengerList();
      this.devices = response.map((e) => {
        e.DeviceStatus = !e.DeviceStatus ? "Unknown" : e.DeviceStatus;
        return e;
      });
    },
    async get_sites() {
      this.network_error = false;
      // let response = await axios_get(scv.get_sites);
      let response = await mobile_grpc_services.GetSites();
      this.planner_groups = {};
      this.asset_id_lookup = {};
      this.sites = response;
      response.forEach((e) => {
        let group = e.planner_group || "Unknown";
        if (group) {
          if (!this.planner_groups[group]) {
            this.planner_groups[group] = 0;
            this.total_planner_groups++;
          }
          this.planner_groups[group] = this.planner_groups[group] + 1;
          this.asset_id_lookup[e.asset_id] = group;
        }
      });
    },
    updateView(view) {
      this.current_view = view;
      this.selected_group = "All";
      this.filter = null;
      this.selected_sites = null;
    },
    apply_donut_filter(device_status) {
      this.filter = device_status;
    },
    apply_group_donut_filter(planner_group) {
      if (!planner_group) this.selected_group = "All";
      else this.selected_group = planner_group;
    },
    apply_site_donut_filter(site_status) {
      this.selected_sites = site_status;
    },
    get_planner_group_stats(sites) {
      if (!sites) return null;
      let temp = {};
      sites.forEach((elem) => {
        let group = elem.planner_group ? elem.planner_group : "Unknown";
        temp[group] = temp[group] ? temp[group] : {};
        temp[group].total_sites = temp[group].total_sites ? temp[group].total_sites + 1 : 1;
        temp[group].active_sites = temp[group].active_sites ? temp[group].active_sites : 0;
        temp[group].inactive_sites = temp[group].inactive_sites ? temp[group].inactive_sites : 0;
        elem.deviceActive ? temp[group].active_sites++ : temp[group].inactive_sites++;
      });
      return temp;
    },
    process_installation_chart_data(list) {
      let temp = [["Months", "Devices Installed", "Total Devices Installation till date"]];
      let temp_data = {};
      let sum = 0;
      list &&
        list.forEach((elem) => {
          let date = elem.StatusDate && elem.DeviceStatus == "Installed" ? new Date(elem.StatusDate) : null;
          if (date) {
            let month = date.toLocaleString("default", { month: "short" });
            let year = date.getFullYear();
            let key = `${month}-${year}`;
            if (!(key in temp_data)) {
              temp_data[key] = [];
            }
            elem.Month = key;
            temp_data[key].push(elem);
          }
        });
      Object.keys(temp_data)
        .sort((a, b) => {
          return new Date(a) - new Date(b);
        })
        .forEach((key) => {
          sum = sum + (temp_data[key].length || 0);
          temp.push([key, temp_data[key].length || 0, sum]);
        });
      return temp;
    },
  },
  computed: {
    filtered_data: function () {
      return this.devices && this.asset_id_lookup
        ? this.devices
            .map((elem) => {
              let planner_group = elem.AssetID ? this.asset_id_lookup[elem.AssetID] : "";
              elem.planner_group = elem.planner_group ? elem.planner_group : planner_group;
              return elem;
            })
            .filter((elem) => {
              return this.selected_group
                ? this.selected_group == "All"
                  ? true
                  : elem.planner_group == this.selected_group
                : true;
            })
            .filter((elem) => {
              return this.filter
                ? this.filter == "Installed"
                  ? elem.DeviceStatus == this.filter
                  : elem.DeviceStatus != "Installed"
                : true;
            })
        : null;
    },
    map_data: function () {
      return this.filtered_data
        ? this.filtered_data
            .filter((elem) => {
              return elem.CurrentLat && elem.CurrentLng ? true : false;
            })
            .map((e) => {
              e.CurrentLat = parseFloat(e.CurrentLat);
              e.CurrentLng = parseFloat(e.CurrentLng);
              return e;
            })
        : null;
    },
    overview_data: function () {
      let data = this.process_installation_chart_data(this.devices);
      return data;
    },
    group_overview_data: function () {
      let data = this.process_installation_chart_data(this.filtered_data);
      return data;
    },
    chartColors: function () {
      let temp = [];
      this.status_data &&
        this.status_data.forEach((elem, index) => {
          let status = elem[0];
          let status_color = this.$config.rollout_status[status];
          let color = this.get_theme_color[status_color];
          index != 0 && temp.push({ color });
        });
      return temp.length > 0 ? temp : null;
    },
    get_theme_color: function () {
      return this.$vuetify.theme.dark ? this.$vuetify.theme.themes.dark : this.$vuetify.theme.themes.light;
    },
    installed_devices: function () {
      return (
        this.devices &&
        this.devices.reduce((acc, elem) => {
          if (elem.DeviceStatus == "Installed") {
            acc++;
          }
          return acc;
        }, 0)
      );
    },
    status_keys: function () {
      return (
        this.devices &&
        this.devices
          .reduce((acc, elem) => {
            let status =
              !elem.DeviceStatus || elem.DeviceStatus.toLowerCase().includes("installed")
                ? "Installed"
                : "Not Installed";
            if (!acc.includes(status)) {
              acc.push(status);
            }
            return acc;
          }, [])
          .sort((a, b) => {
            return a.localeCompare(b);
          })
      );
    },
    all_planner_groups: function () {
      return this.planner_groups ? ["All"].concat(Object.keys(this.planner_groups)) : null;
    },
    planner_group_donut: function () {
      let status_data = [
        [
          "Planner Group",
          "Devices",
          {
            role: "tooltip",
            type: "string",
            p: {
              html: true,
            },
          },
        ],
      ];
      this.planner_groups &&
        Object.keys(this.planner_groups).length > 0 &&
        Object.keys(this.planner_groups).forEach((elem) => {
          status_data.push([
            elem,
            this.planner_groups[elem],
            {
              footer: `${this.planner_groups[elem]}(${(
                (this.planner_groups[elem] / this.total_planner_groups) *
                100
              ).toFixed(0)}%)`,
              msg: `Devices to be installed in ${elem}`,
              title: elem,
            },
          ]);
        });
      return !this.planner_groups || Object.keys(this.planner_groups).length === 0 ? null : status_data;
    },
    planner_group_status: function () {
      if (!this.sites || !this.planner_groups || !this.all_planner_groups_list) return null;
      let status_data = [["Planner Group", "Active Sites", "Inactive Sites", { role: "annotation" }]];
      this.all_planner_groups_list.length > 0 &&
        this.all_planner_groups_list.forEach((elem) => {
          status_data.push([elem.planner_group, elem.active_sites, elem.inactive_sites, elem.total_sites]);
        });
      return status_data;
    },
    status_data: function () {
      let temp = {};
      let status_data = [
        [
          "Status",
          "Count",
          {
            role: "tooltip",
            type: "string",
            p: {
              html: true,
            },
          },
        ],
      ];
      this.devices &&
        this.devices.forEach((elem) => {
          let status =
            !elem.DeviceStatus || elem.DeviceStatus.toLowerCase().includes("installed") ? "Installed" : "Not Installed";
          if (!(status in temp)) {
            temp[status] = [];
          }
          temp[status].push(elem);
        });
      this.devices &&
        this.status_keys &&
        this.status_keys.map((key) => {
          let value = temp[key] ? temp[key].length : 0;
          status_data.push([
            key,
            value,
            {
              footer: `${value}(${((value / this.devices.length) * 100).toFixed(0)}%)`,
              msg: `Devices with ${key} status`,
              title: key,
            },
          ]);
        });
      return Object.keys(temp).length === 0 && temp.constructor === Object ? null : status_data;
    },
    site_status_data: function () {
      let temp = {};
      let status_data = [
        [
          "Status",
          "Count",
          {
            role: "tooltip",
            type: "string",
            p: {
              html: true,
            },
          },
        ],
      ];
      this.formatted_sites &&
        this.formatted_sites.forEach((elem) => {
          let status = elem.deviceActive ? "Installed" : "Not Installed";
          if (!(status in temp)) {
            temp[status] = [];
          }
          temp[status].push(elem);
        });
      this.formatted_sites &&
        this.status_keys &&
        this.status_keys.map((key) => {
          let value = temp[key] ? temp[key].length : 0;
          status_data.push([
            key,
            value,
            {
              footer: `${value}(${((value / this.formatted_sites.length) * 100).toFixed(0)}%)`,
              msg: `Sites with ${key} devices`,
              title: key,
            },
          ]);
        });
      return Object.keys(temp).length === 0 && temp.constructor === Object ? null : status_data;
    },
    installed_devices_assetids: function () {
      return this.devices
        ? this.devices
            .filter((elem) => {
              return elem.DeviceStatus == "Installed";
            })
            .map((elem) => {
              return elem.AssetID;
            })
        : null;
    },
    formatted_sites: function () {
      return this.sites && this.installed_devices_assetids
        ? this.sites.map((e) => {
            e.planner_group = e.planner_group ? e.planner_group : "Unknown";
            e.deviceActive = this.installed_devices_assetids.includes(e.asset_id) || false;
            return e;
          })
        : null;
    },
    installed_sites: function () {
      return this.formatted_sites
        ? this.formatted_sites.filter((elem) => {
            return elem.deviceActive;
          })
        : [];
    },
    filtered_sites: function () {
      return this.formatted_sites
        ? this.formatted_sites
            .filter((elem) => {
              return this.selected_group
                ? this.selected_group == "All"
                  ? true
                  : elem.planner_group == this.selected_group
                : true;
            })
            .filter((elem) => {
              return this.selected_sites
                ? this.selected_sites == "Installed"
                  ? elem.deviceActive
                  : !elem.deviceActive
                : true;
            })
        : null;
    },
    map_formatted_sites: function () {
      return this.filtered_sites
        ? this.filtered_sites.filter((elem) => {
            return elem.latitude && elem.longitude ? true : false;
          })
        : null;
    },
    current_stat: function () {
      let temp = {
        devices: { count: this.devices ? this.devices.length : 0, installed: this.installed_devices },
        planner_groups: { count: this.all_planner_groups ? this.all_planner_groups.length - 1 : 0 },
        sites: {
          count: this.sites ? this.sites.length : 0,
          installed: this.installed_sites ? this.installed_sites.length : 0,
        },
      };
      return temp;
    },
    all_planner_groups_list: function () {
      let temp;
      let list = [];
      if (this.planner_groups && this.formatted_sites) temp = this.get_planner_group_stats(this.formatted_sites);
      Object.keys(temp).forEach((elem) => {
        temp[elem]["planner_group"] = elem;
        list.push(temp[elem]);
      });
      return list;
    },
    planner_groups_list: function () {
      let list = [];
      let temp;
      if (this.planner_groups && this.filtered_sites) temp = this.get_planner_group_stats(this.filtered_sites);
      Object.keys(temp).forEach((elem) => {
        temp[elem]["planner_group"] = elem;
        list.push(temp[elem]);
      });
      return list;
    },
    devices_data_available: function () {
      return this.devices && this.devices.length > 0;
    },
  },
};
</script>
<style lang="scss" scoped>
::v-deep .export-div {
  top: 35px;
}

::v-deep .right-border {
  border-right: 1px solid var(--v-border-base);
}

::v-deep .v-chip {
  // transition: visibility 0s linear 0.33s, opacity 0.33s linear;
  .show-on-hover {
    transform: scaleX(0);
    overflow: hidden;
    width: 0;
  }
}
::v-deep .v-chip:hover {
  transform: scaleX(1);
  transition: width 0.5s ease;
  .show-on-hover {
    transform: scaleX(1);
    width: auto;
    overflow: visible;
  }
}
.banner {
  height: 100px;
}

.common-card-col {
  div.title {
    font-family: Trade Gothic Regular !important;
    font-size: 1.25rem;
    font-weight: 500;
    letter-spacing: 0.0125em;
    line-height: 2rem;
    word-break: break-all;
  }
}
.opaque-img {
  opacity: 0.7;
}
.filters-con .v-card.filter-con:last-of-type {
  margin-bottom: 0px !important;
}
</style>
